import React, { useEffect, useState } from 'react';
import { Save, Sparkles } from 'lucide-react';
import HourglassLoader from './ui/HourglassLoader';
import apiClient from '../lib/apiClient';
import { FontPicker } from '../domain/fonts';
import { MediaPicker } from '../domain/media';
import './BrandIdentity.css';

function BrandIdentity() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoPrompt, setLogoPrompt] = useState('');
  const [form, setForm] = useState({
    logo_url: '',
    logo_background_image: null,
    colors: { primary: '#06B6D4', secondary: '#1E40AF', accent: '#7E22CE' },
    font_primary: 'Inter',
    font_secondary: 'Poppins',
    visual_style: 'moderno'
  });

  const primaryFontClass = `font-${(form.font_primary || 'inter').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const secondaryFontClass = `font-${(form.font_secondary || 'inter').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiClient.request('/user/brand-identity');
        if (data?.success && data.brand_identity) {
          setForm((prev) => ({ ...prev, ...data.brand_identity, colors: data.brand_identity.colors || prev.colors }));
        }
      } catch (error) {
        console.error('Erro ao carregar identidade:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.request('/user/brand-identity', { method: 'POST', body: JSON.stringify(form) });
    } catch (error) {
      console.error('Erro ao salvar identidade:', error);
    } finally {
      setSaving(false);
    }
  };

  const generateLogo = async () => {
    if (!logoPrompt.trim()) return;
    try {
      const data = await apiClient.request('/generate-logo', {
        method: 'POST',
        auth: false,
        body: JSON.stringify({ prompt: logoPrompt })
      });
      if (data?.success) {
        const logoUrl = Array.isArray(data.logos) ? data.logos[0] : data.logo_url;
        setForm((prev) => ({ ...prev, logo_url: logoUrl || prev.logo_url }));
        setLogoPrompt('');
      }
    } catch (error) {
      console.error('Erro ao gerar logo:', error);
    }
  };

  if (loading) {
    return <HourglassLoader variant="inline" message="Carregando identidade visual..." />;
  }

  return (
    <div className="brand-identity-page">
      <div className="identity-header">
        <button className="btn btn-outline" onClick={save} disabled={saving}>
          <div className="blob1"></div>
          <div className="blob2"></div>
          <div className="inner"><Save size={16} /> {saving ? 'Salvando...' : 'Salvar'}</div>
        </button>
      </div>

      <div className="identity-grid">
        <section className="card identity-card">
          <h3>Logo</h3>
          <div className="input-group">
            <input
              value={logoPrompt}
              placeholder="Descreva a logo desejada"
              onChange={(event) => setLogoPrompt(event.target.value)}
            />
          </div>
          <button className="btn btn-secondary" onClick={generateLogo}>
            <Sparkles size={14} /> Gerar logo
          </button>
          <div className="input-group">
            <label>Fonte principal</label>
            <FontPicker
              label="Fonte principal"
              value={{ family: form.font_primary, weight: '600' }}
              onChange={(font) => setForm((prev) => ({ ...prev, font_primary: font.family }))}
            />
          </div>
          <div className="input-group">
            <label>Fonte secundária</label>
            <FontPicker
              label="Fonte secundária"
              value={{ family: form.font_secondary, weight: '400' }}
              onChange={(font) => setForm((prev) => ({ ...prev, font_secondary: font.family }))}
            />
          </div>
          <div className="brand-font-preview">
            <p className={`brand-font-preview__primary ${primaryFontClass}`}>Marketing Pro</p>
            <small className={`brand-font-preview__secondary ${secondaryFontClass}`}>Sua marca, sua tipografia</small>
          </div>
          {form.logo_url && <img src={form.logo_url} alt="Logo da marca" className="logo-preview-img" />}

          <div className="input-group">
            <label>Background da Logo (Pexels)</label>
            <MediaPicker
              type="photo"
              value={form.logo_background_image}
              onChange={(image) => setForm((prev) => ({ ...prev, logo_background_image: image }))}
            />
            {form.logo_background_image && (
              <small className="brand-identity-credit">
                Foto por {form.logo_background_image.author || 'Pexels'} ·
                {' '}
                <a href={form.logo_background_image.author_url || 'https://www.pexels.com'} target="_blank" rel="noreferrer">Pexels</a>
              </small>
            )}
          </div>
        </section>

        <section className="card identity-card">
          <h3>Cores</h3>
          <div className="input-group">
            <label>Primária</label>
            <input type="color" value={form.colors.primary} onChange={(event) => setForm((prev) => ({ ...prev, colors: { ...prev.colors, primary: event.target.value } }))} />
          </div>
          <div className="input-group">
            <label>Secundária</label>
            <input type="color" value={form.colors.secondary} onChange={(event) => setForm((prev) => ({ ...prev, colors: { ...prev.colors, secondary: event.target.value } }))} />
          </div>
          <div className="input-group">
            <label>Destaque</label>
            <input type="color" value={form.colors.accent} onChange={(event) => setForm((prev) => ({ ...prev, colors: { ...prev.colors, accent: event.target.value } }))} />
          </div>
        </section>
      </div>
    </div>
  );
}

export default BrandIdentity;
